import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createAppTheme } from "./theme";

type Mode = "light" | "dark";

type ThemeModeContextValue = {
    mode: Mode;
    toggle: () => void;
    setMode: (m: Mode) => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

export function useThemeMode() {
    const ctx = useContext(ThemeModeContext);
    if (!ctx) throw new Error("useThemeMode must be used within ThemeModeProvider");
    return ctx;
}

const STORAGE_KEY = "theme-mode";

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
    const [mode, setMode] = useState<Mode>("light");

    // load saved mode (or system preference)
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY) as Mode | null;
        if (saved === "light" || saved === "dark") {
            setMode(saved);
            return;
        }
        const prefersDark =
            window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
        setMode(prefersDark ? "dark" : "light");
    }, []);

    // persist
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, mode);
    }, [mode]);

    const theme = useMemo(() => createAppTheme(mode), [mode]);

    const value = useMemo(
        () => ({
            mode,
            toggle: () => setMode((m) => (m === "light" ? "dark" : "light")),
            setMode,
        }),
        [mode]
    );

    return (
        <ThemeModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
}
